import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as orderService from "../services/orderService";
import { message } from "antd";

export const useOrders = (params = {}) => {
  return useQuery({
    queryKey: ["orders", params],
    queryFn: () => orderService.getOrdersList(params),
    staleTime: 30 * 1000,
  });
};

export const useOrder = (id) => {
  return useQuery({
    queryKey: ["order", id],
    queryFn: () => orderService.getOrder(id),
    enabled: !!id,
  });
};

export const useOrderStats = (params = {}) => {
  return useQuery({
    queryKey: ["orderStats", params],
    queryFn: () => orderService.getOrderStats(params),
    staleTime: 60 * 1000,
  });
};

export const useOrdersNeedingFulfillment = () => {
  return useQuery({
    queryKey: ["ordersNeedingFulfillment"],
    queryFn: () => orderService.getOrdersNeedingFulfillment(),
    staleTime: 30 * 1000,
  });
};

export const useOrderMutations = () => {
  const queryClient = useQueryClient();

  const createOrder = useMutation({
    mutationFn: orderService.createOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orderStats"] });
      message.success("Order created successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.error || "Failed to create order");
    },
  });

  const updateOrder = useMutation({
    mutationFn: ({ id, order }) => orderService.updateOrder(id, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order"] });
      message.success("Order updated successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.error || "Failed to update order");
    },
  });

  const updateOrderStatus = useMutation({
    mutationFn: ({ id, status }) => orderService.updateOrderStatus(id, status),
    onSuccess: async () => {
      // Force immediate refetch of all order-related queries
      await queryClient.refetchQueries({ queryKey: ["orders"] });
      await queryClient.refetchQueries({ queryKey: ["orderStats"] });
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["ordersNeedingFulfillment"] });
      message.success("Order status updated");
    },
    onError: (error) => {
      message.error(error.response?.data?.error || "Failed to update status");
    },
  });

  const addTracking = useMutation({
    mutationFn: ({ id, trackingNumber, carrier }) =>
      orderService.addTracking(id, trackingNumber, carrier),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order"] });
      message.success("Tracking information added");
    },
    onError: (error) => {
      message.error(error.response?.data?.error || "Failed to add tracking");
    },
  });

  const deleteOrder = useMutation({
    mutationFn: orderService.deleteOrder,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["orders"] });
      await queryClient.refetchQueries({ queryKey: ["orderStats"] });
      message.success("Order deleted successfully");
    },
    onError: (error) => {
      message.error(error.response?.data?.error || "Failed to delete order");
    },
  });

  return {
    createOrder,
    updateOrder,
    updateOrderStatus,
    addTracking,
    deleteOrder,
  };
};
